import React, {
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";
import { redirect, useNavigate } from "react-router";
import { loginService as aFLoginService } from "core/services/ac2Flywheel/authService";
import { getProvidersFromBackend } from "modules/chat/services/chatService";
import { useAppDispatch } from "core/store/reduxStore";
import {
  fetchTokenService as amsTokenService,
  userService,
  logoutService,
  checkTokenService,
} from "core/services/agentMySecurity/authService";

import { loginService as amsLoginService } from "core/services/agentMySecurity/authService";
import { Toast as showToast } from "core/components/global/toast/toast";
import { setUserService } from "core/services/ac2Flywheel/userService";
import { SessionExpiredOverlay } from "core/components/global/SessionExpiredOverlay/SessionExpiredOverlay";
import { setProviders } from "modules/chat/store/conversationList";

export type UserContextType = {
  afUser: UserClaims | null;
  setAFUser: Dispatch<SetStateAction<UserClaims | null>>;
  amsUser: UserClaims | null;
  setAMSUser: Dispatch<SetStateAction<UserClaims | null>>;
  saveUser: any;
  userAdGroups: string[];
  setUserAdGroups: Dispatch<SetStateAction<string[]>>;
  amsBearerToken: string | null;
  setAMSBearerToken: Dispatch<SetStateAction<string>>;
  aFAccessToken: string | null;
  setAFAccessToken: Dispatch<SetStateAction<string>>;
  aFRefreshToken: string | null;
  setAFRefreshToken: Dispatch<SetStateAction<string | null>>;
  loginAF: () => void;
  loginAMS: () => void;
  signOut: () => void;
  isAvailableAuthUser: boolean;
  setIsAvailableAuthUser: Dispatch<SetStateAction<boolean>>;
  setAMSToken: any;
  setAFToken: any;
  getAFUser: any;
  checkAFToken: any;
  checkAMSToken: any;
  getAMSUser: any;
  initializeAuth: () => Promise<void>;
  isAuthInitialized: boolean;
};

const initialState: UserContextType = {
  afUser: null,
  setAFUser: () => undefined,
  amsUser: null,
  setAMSUser: () => undefined,
  userAdGroups: [],
  setUserAdGroups: () => undefined,
  amsBearerToken: null,
  setAMSBearerToken: () => undefined,
  aFAccessToken: null,
  setAFAccessToken: () => undefined,
  aFRefreshToken: null,
  setAFRefreshToken: () => undefined,
  saveUser: () => undefined,
  loginAF: () => undefined,
  loginAMS: () => undefined,
  signOut: () => undefined,
  isAvailableAuthUser: false,
  setIsAvailableAuthUser: () => undefined,
  getAFUser: () => undefined,
  setAMSToken: () => undefined,
  setAFToken: () => undefined,
  checkAFToken: () => undefined,
  checkAMSToken: () => undefined,
  getAMSUser: () => undefined,
  initializeAuth: () => Promise.resolve(),
  isAuthInitialized: false,
};

export interface UserClaims {
  sub: string;
  given_name: string;
  family_name: string;
  email_verified: boolean;
  preferred_username: string;
  auth_time?: Date;
  aud?: string;
  lastLoggedIn?: Date;
  image?: string;
  firstLetter?: string;
  email: string;
  groups?: string[];
  username?: string;
}

export const AuthContext = createContext<UserContextType>(initialState);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [afUser, setAFUser] = useState<UserClaims | null>(null);
  const [amsUser, setAMSUser] = useState<any>(null);
  const [amsBearerToken, setAMSBearerToken] = useState<string>("");
  const [aFAccessToken, setAFAccessToken] = useState<string>("");
  const [aFRefreshToken, setAFRefreshToken] = useState<string | null>(null);
  const [isAvailableAuthUser, setIsAvailableAuthUser] =
    useState<boolean>(false);
  const [userAdGroups, setUserAdGroups] = useState<string[]>([]);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  
  // Track token refresh operations
  const isRefreshing = useRef<boolean>(false);
  const lastRefreshTime = useRef<number>(0);

  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour to match cookie expiration

  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionTimer = useRef<NodeJS.Timeout>();

  const getAMSUser = async () => {
    console.log(`getAMSUser: `)
    try {
      // Extract the data from the Axios response
      const resp = await userService();

      // Set the user so we can soft prevent them in the UI, invalid authentication
      // will be blocked by the backend anyway
      setAMSUser(resp);

      return resp;
    } catch (error) {
      console.error("Error fetching AMS user:", error);
      return null;
    }
  };

  const setAMSToken = async () => {
    // console.log("setAMSToken");
    try {
      // Fetch the token from auth/token
      const response = await amsTokenService();
      const data = response.data;
      let token = "bearer " + data.token;

      // Set the token into the Auth context for global usage
      setAMSBearerToken(token);

      // Return the token value
      return token;
    } catch (error) {
      return null;
    }
  };

  const checkAMSToken = async () => {
    // Prevent concurrent token checks/refreshes
    console.log(`checkAMSToken, debugging getAMSUser 0: `, amsBearerToken);

    // If we already have a valid token, return it immediately
    if (amsBearerToken && amsBearerToken !== "" && amsBearerToken.includes("bearer ")) {
      // Quick local check to see if token is still valid based on expiry
      try {
        const tokenPart = amsBearerToken.split(' ')[1];
        const base64Url = tokenPart.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        
        // Check if token has more than 30 seconds until expiry
        if (payload.exp && payload.exp > (Date.now() / 1000) + 30) {
          console.log('Using existing valid token');
          return amsBearerToken;
        }
      } catch (e) {
        // Error parsing token, continue with validation
        console.log('Error checking token expiry, will validate with server');
      }
    }
    
    if (isRefreshing.current) {
      console.log(`Token refresh already in progress, skipping ${amsBearerToken}`);
      
      // Wait for the refresh to complete
      await new Promise(resolve => setTimeout(resolve, 750));
      console.log(`After waiting for refresh, token is now: ${amsBearerToken}`);
      return amsBearerToken;
    }
    
    // Add a time-based throttle (prevent refreshes within 3 seconds)
    const now = Date.now();
    // if (now - lastRefreshTime.current < 3000) {
    //   console.log('Token refresh throttled, too soon since last refresh');
    //   return amsBearerToken;
    // }
    
    try {
      isRefreshing.current = true;
      lastRefreshTime.current = now;
      
      console.log(`checkAMSToken, debugging getAMSUser 1: `, amsBearerToken);
      
      // If token is empty, attempt to get a new one rather than failing immediately
      if (amsBearerToken === undefined || amsBearerToken === "") {
        console.log(`checkAMSToken, debugging getAMSUser 2: `, amsBearerToken);
        const new_token_response = await setAMSToken();
        if (new_token_response) {
          return new_token_response;
        }
        return null;
      }
  
      let response;
      let retryCount = 0;
      const maxRetries = 2;
  
      while (retryCount < maxRetries) {
        console.log(`checkAMSToken, debugging getAMSUser retry ${retryCount + 1}: `, amsBearerToken);
        try {
          // Fetch the token from auth/token
          response = await checkTokenService(amsBearerToken.split(" ")[1]);
  
          // Is the token valid (status < 300)
          if (response && response.status < 300) {
            console.log(`checkAMSToken, debugging getAMSUser 4: `, amsBearerToken);
            // Return the existing token since it's valid
            return amsBearerToken;
          } else {
            console.log(`checkAMSToken, debugging getAMSUser 5: `, amsBearerToken);
            retryCount = 2;
            // Token is invalid, try to get a new one
            break;
          }
        } catch (error) {
          console.log(
            `Token verification error (attempt ${retryCount + 1}/${maxRetries}):`,
            error
          );
          retryCount++;
  
          // If we've reached max retries, break out of the loop
          if (retryCount >= maxRetries) {
            break;
          }
  
          // Wait briefly before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
  
      // If we get here, the token is invalid or verification failed multiple times
      // Try to get a new token
      try {
        console.log(`checkAMSToken, debugging getAMSUser 3: `, amsBearerToken);
        const new_token_response = await setAMSToken();
  
        // If it failed, return null but don't immediately redirect
        if (new_token_response === null) {
          console.log("Failed to get a new token");
          return null;
        } else {
          return new_token_response;
        }
      } catch (error) {
        console.error("Error getting new token:", error);
        return null;
      }
    } finally {
      isRefreshing.current = false;
    }
  };

  const getAFUser = async () => {
    // Retrieve JWT token from browser storage (assuming it's stored in localStorage for this example)
    // Function to retrieve JWT token and user info
    try {
      // // Make a request to your backend to get the JWT token
      // const data = await assertionService();

      // // Extract the Assertions from the IDP
      // const assertions = data.assertion.Assertion;

      // let given_name = '';
      // let family_name = '';
      // const attributes = assertions.AttributeStatement[0].Attribute;
      // attributes.forEach(attr => {
      //   if (attr.$.Name === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname') {
      //     given_name = attr.AttributeValue[0]._;
      //   }
      //   if (attr.$.Name === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname') {
      //     family_name = attr.AttributeValue[0]._;
      //   }
      // });

      // const email = data.email
      // const email_verified = email
      // const preferred_username = data.username
      // const sub = assertions.Subject[0].NameID[0]._;

      // return { sub, given_name, family_name, email, email_verified, preferred_username };
      return {
        sub: "",
        given_name: "",
        family_name: "",
        email: "",
        email_verified: "",
        preferred_username: "",
      };
    } catch (error) {
      console.error("getAFUser Error decoding JWT token:", error);
      return null;
    }
  };

  const setAFToken = async () => {
    try {
      // // Make a request to your backend to get the JWT token
      // const data = await afTokenService();
      // const token = "bearer " + data.token

      // // Set the access token here so on every service call it can just be pulled
      // setAFAccessToken(token)

      // // Return the token value
      // return token

      // const token = await getToken();
      const token = "bearer something";
      setAFAccessToken(token);
      return token;
    } catch (error) {
      return null;
    }
  };

  const saveUser = async ({ givenUser }) => {
    setAFUser(givenUser);
  };

  const checkAFToken = async () => {
    // try {
    //   const tokenMinusBearer = aFAccessToken.substring(7)
    //   // Decode the token minus 'bearer '
    //   const token = jwtDecode(tokenMinusBearer);
    //   const now = Math.floor(Date.now() / 1000);

    //   // Check if the token has expired
    //   if (token.exp && now > token.exp) {
    //     // Token has expired
    //     const token = await setAFToken();
    //     return token;
    //   } else {
    //     // Token is valid
    //     return aFAccessToken;
    //   }
    // } catch (error) {
    //   showToast("Unable to fetch a valid JWT token at this time", "error")
    //   console.error('checkAFToken error:', error);
    //   return null;
    // }
    return "bearer something";
  };

  const loginAF = async () => {
    try {
      aFLoginService();
    } catch (error) {
      navigate("/login/ac2flywheel");
    }
  };

  const loginAMS = async () => {
    try {
      // TODO redirect to our backend, once available
      amsLoginService();

      // TODO delete the following, once the backend is available
      // const token = await checkAMSToken();
      // if (token) {
      //   navigate("/console")
      // }
    } catch (error) {
      console.log(`check token 5`);
      navigate("/console/login");
    }
  };

  const signOut = async () => {
    // Clear user session on the client
    setAMSUser(null);
    setAMSBearerToken("");
    setAFUser(null);
    setAFAccessToken("");
    setAFRefreshToken(null);

    logoutService();
    // Redirect the user to the IDP
    // TODO logoutService() as a redirect once we have an env specific backend; & remove the below
    // const response = await logoutService();
    // if (response === true) {
    //   navigate("/console/login");
    // } else {
    //   navigate("/console/login");
    // }
  };

  const handleRelogin = async () => {
    try {
      setSessionExpired(false);
      navigate("/console/login");
    } catch (error) {
      console.error("Relogin failed:", error);
      navigate("/console/login");
    }
  };

  useEffect(() => {
    if (amsUser && amsBearerToken !== "") {
      /*
          After a successfull Ac2/SOC AI login, make sure that the user and session is reflected in the
          backend db. This will assist with managing features such as user comments or user
          profiles.
        */
      try {
        // Extract first name and last name from username if available
        let firstName = "";
        let lastName = "";

        if (amsUser.username && amsUser.username.includes(",")) {
          // Format is usually "Last, First" - split and trim
          const nameParts = amsUser.username.split(",");
          lastName = nameParts[0].trim();
          firstName = nameParts.length > 1 ? nameParts[1].trim() : "";
        }

        // Add user to AC2 database
        setUserService(
          { Authorization: amsBearerToken, "Content-Type": "application/json" },
          amsUser.email,
          firstName,
          lastName,
          Date.now(),
          "ac2"
        ).catch((error) => {
          console.log("Error updating AC2 user service:", error);
          // Silent fail - don't disrupt the user experience
        });

        // Add user to Flywheel database
        setUserService(
          { Authorization: amsBearerToken, "Content-Type": "application/json" },
          amsUser.email,
          firstName,
          lastName,
          Date.now(),
          "socai"
        ).catch((error) => {
          console.log("Error updating user service:", error);
          // Silent fail - don't disrupt the user experience
        });
      } catch (error) {
        console.log("Error in setUserService:", error);
        // Silent fail - don't disrupt the user experience
      }
    }
  }, [amsUser, amsBearerToken])

  // This is commented out to avoid conflicts with the SessionTimeout component
  // which already manages the session timeout using constants.ts settings
  /*
    useEffect(() => {
      const resetSessionTimer = () => {
        if (sessionTimer.current) {
          clearTimeout(sessionTimer.current);
        }
        sessionTimer.current = setTimeout(() => {
          setSessionExpired(true);
          // When session expires, clear tokens to prevent further API calls
          setAMSBearerToken("");
          setAFAccessToken("");
        }, SESSION_TIMEOUT);
      };

      // Reset timer on user activity
      const handleUserActivity = () => {
        if (!sessionExpired) {
          resetSessionTimer();
        }
      };

      window.addEventListener('mousedown', handleUserActivity);
      window.addEventListener('keydown', handleUserActivity);
      window.addEventListener('scroll', handleUserActivity);

      resetSessionTimer();

      return () => {
        if (sessionTimer.current) {
          clearTimeout(sessionTimer.current);
        }
        window.removeEventListener('mousedown', handleUserActivity);
        window.removeEventListener('keydown', handleUserActivity);
        window.removeEventListener('scroll', handleUserActivity);
      };
    }, [sessionExpired]);
    */

  const initializeOther = async (token) => {
      // Get user info
      const amsUserInfo = await getAMSUser();
      if (amsUserInfo) {
        setIsAvailableAuthUser(true);
        
        // Get provider names if needed
        try {
          // Get provider names for the dropdown
          console.log('Fetching provider names with token');
          const names = await getProvidersFromBackend(token || amsBearerToken);
          
          console.log("Provider names fetched:", names);

          if (names) {
            dispatch(setProviders({ providers: names }));
          }
        } catch (error) {
          console.error('Error fetching providers:', error);
        }
      }
      
      // Get AF user info
      const afUserInfo = await getAFUser();
      if (afUserInfo) {
        // Set AF token
        await setAFToken();
        
        // Set user data
        setAFUser({
          sub: afUserInfo.sub,
          given_name: afUserInfo.given_name,
          family_name: afUserInfo.family_name,
          email: afUserInfo.email,
          email_verified: typeof afUserInfo.email_verified === 'string' ? afUserInfo.email_verified === 'true' : !!afUserInfo.email_verified,
          preferred_username: afUserInfo.preferred_username,
        });
      } else {
        console.log('No valid AF user, redirecting to login');
        navigate('/console/login');
        return;
      }
      
      setIsAuthInitialized(true);
      console.log('Auth initialization complete');
  }

  // Centralized auth initialization method
  const initializeAuth = async () => {
    console.log('Initializing authentication...');
    try {
      // First check for AMS token
      const token = await checkAMSToken();
      
      console.log(`initializeAuth received token: ${token ? 'yes' : 'no'}, value: ${token}`);
      
      if (!token) {
        // We don't have a token - try up to 3 times with a delay
        // This helps with SAML redirects where token might not be immediately available
        for (let attempt = 0; attempt < 3; attempt++) {
          console.log(`No token found, retry attempt ${attempt + 1}/3. Current token state:`, amsBearerToken);
          await new Promise(resolve => setTimeout(resolve, 750));
          
          // Check if token appeared in state while we were waiting
          if (amsBearerToken && amsBearerToken !== "") {
            console.log(`Token appeared in state during wait: ${amsBearerToken}`);
            await initializeOther(amsBearerToken);
            return;
          }
          
          const retryToken = await checkAMSToken();
          console.log(`Retry ${attempt + 1} returned token: ${retryToken ? 'yes' : 'no'}`);
          
          if (retryToken) {
            console.log('Token obtained on retry:', retryToken);
            await initializeOther(retryToken);
            return;
          }
        }
        
        // If we get here, we failed to get a token after retries
        console.log('Failed to get token after retries, redirecting to login. Final token state:', amsBearerToken);
        navigate('/console/login');
        return;
      } else {
        console.log('Valid token found on first attempt:', token);
        await initializeOther(token);
      }
    } catch (error) {
      console.error('Error during auth initialization:', error);
      navigate('/console/login');
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        saveUser,
        afUser,
        setAFUser,
        amsUser,
        setAMSUser,
        userAdGroups,
        setUserAdGroups,
        amsBearerToken,
        setAMSBearerToken,
        aFAccessToken,
        setAFAccessToken,
        aFRefreshToken,
        setAFRefreshToken,
        loginAF,
        signOut,
        loginAMS,
        isAvailableAuthUser,
        setIsAvailableAuthUser,
        getAFUser,
        setAFToken,
        setAMSToken,
        checkAFToken,
        checkAMSToken,
        getAMSUser,
        initializeAuth,
        isAuthInitialized,
      }}
    >
      {children}
      {sessionExpired && <SessionExpiredOverlay onRelogin={handleRelogin} />}
    </AuthContext.Provider>
  );
};
